// src/pages/employee/TeleCallingSales.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';

const TeleCallingSalesManagement = () => {
  const [records, setRecords] = useState([]);
  const [stations, setStations] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filteredDealers, setFilteredDealers] = useState([]);
  const [searchByStation, setSearchByStation] = useState('');
  const [searchByDealer, setSearchByDealer] = useState('');
  const { user } = useContext(AuthContext);
  const [form, setForm] = useState({
    station: '',
    recipientType: '',
    recipient: '',
    callResponse: '',
    remarks: '',
    reminders: {message: '', date: ''},
    createdBy: user?.user?._id
  });

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/tele-calling-sales/${user.user._id}`);
        const today = new Date().toISOString().split('T')[0];
        const filteredRecords = response.data.filter(record => record.createdAt.split('T')[0] === today);
        setRecords(filteredRecords);
      } catch (error) {
        Swal.fire('Error', 'Failed to fetch tele-calling sales records', 'error');
      }
    }
    fetchRecords();
    fetchStations();
    fetchDealers();
    fetchCustomers();
  }, [user]);

  const fetchRecords = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/tele-calling-sales/${user.user._id}`);
      const today = new Date().toISOString().split('T')[0];
      const filteredRecords = response.data.filter(record => record.createdAt.split('T')[0] === today);
      setRecords(filteredRecords);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch tele-calling sales records', 'error');
    }
  };

  const fetchStations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/stations`);
      setStations(response.data);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch stations', 'error');
    }
  };

  const fetchDealers = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/dealers`);
      setDealers(response.data);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch dealers', 'error');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/customers`);
      setCustomers(response.data);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch customers', 'error');
    }
  };

  const validateForm = () => {
    if (!form.station || !form.recipientType || !form.recipient || !form.callResponse || !form.remarks) {
      Swal.fire('Warning', 'All fields are required', 'warning');
      return false;
    }
    if (form.reminders.message && !form.reminders.date) {
      Swal.fire('Warning', 'Please add reminder date', 'warning');
      return false;
    } 
     if (form.reminders.date  && !form.reminders.message) {
      Swal.fire('Warning', 'Please add reminder message', 'warning');
      return false;
    }
    return true;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('reminders.')) {
      const field = name.split('.')[1];
      setForm(prev => ({
        ...prev,
        reminders: {
          ...prev.reminders,
          [field]: value
        }
      }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
      
      if (name === 'station') {
        const filtered = dealers.filter(dealer => dealer.station._id === value);
        setFilteredDealers(filtered);
      }
    }
  };

  const resetForm = () => {
    setForm({
      station: '',
      recipientType: '',
      recipient: '',
      callResponse: '',
      remarks: '',
      reminders: {message: '', date: ''},
      createdBy: user?.user?._id
    });
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/tele-calling-sales`, form);
      Swal.fire('Success', 'Record created successfully', 'success');
      fetchRecords();
      resetForm();
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Failed to save record', 'error');
    }
  };

  return (
    <div className="p-4 lg:p-6">
      <h2 className="text-2xl font-bold mb-6">Tele-Calling Sales </h2>
    
      <div className="bg-white rounded-lg shadow-md p-4 lg:p-6 mb-6">
        <h3 className="text-xl font-semibold mb-4">
          Add New Record
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="flex flex-col">
            <label className="mb-1 text-gray-700">Station*</label>
            <select
              name="station"
              value={form.station}
              onChange={handleInputChange}
              className="p-2 border rounded focus:ring focus:ring-blue-200"
            >
              <option value="">Select Station</option>
              {stations.map(station => (
                <option key={station._id} value={station._id}>{station.name}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="mb-1 text-gray-700">Recipient Type*</label>
            <select
              name="recipientType"
              value={form.recipientType}
              onChange={handleInputChange}
              className="p-2 border rounded focus:ring focus:ring-blue-200"
            >
              <option value="">Select Recipient Type</option>
              <option value="DEALER">DEALER</option>
              <option value="CUSTOMER">CUSTOMER</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-gray-700">Recipient*</label>
            <select
              name="recipient"
              value={form.recipient}
              onChange={handleInputChange}
              className="p-2 border rounded focus:ring focus:ring-blue-200"
            >
              <option value="">Select Recipient</option>
              {form.recipientType === 'DEALER'
                ? filteredDealers.map(dealer => (
                    <option key={dealer._id} value={dealer._id}>{dealer.businessName}</option>
                  ))
                : customers.map(customer => (
                    <option key={customer._id} value={customer._id}>{customer.name}</option>
                  ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="mb-1 text-gray-700">Call Response*</label>
            <select
              name="callResponse"
              value={form.callResponse}
              onChange={handleInputChange}
              className="p-2 border rounded focus:ring focus:ring-blue-200"
            >
              <option value="">Select Response</option>
              <option value="RECEIVED">RECEIVED</option>
              <option value="NO REPLY">NO REPLY</option>
              <option value="BUSY">BUSY</option>
              <option value="NOT REACHABLE">NOT REACHABLE</option>
              <option value="SWITCH OFF">SWITCH OFF</option>
              <option value="NUMBER DOES NOT EXIST">NUMBER DOES NOT EXIST</option>
            </select>
          </div>

          <div className="flex flex-col">
            <label className="mb-1 text-gray-700">Remarks*</label>
            <textarea
              name="remarks"
              value={form.remarks}
              onChange={handleInputChange}
              className="p-2 border rounded focus:ring focus:ring-blue-200"
            />
          </div>

          <div className="flex flex-col">
            <label className="mb-1 text-gray-700">Reminder Message</label>
            <input
              type="text"
              name="reminders.message"
              value={form.reminders.message}
              onChange={handleInputChange}
              className="p-2 border rounded focus:ring focus:ring-blue-200"
            />
          </div>

          <div className="flex flex-col">
            <label className="mb-1 text-gray-700">Reminder Date</label>
            <input
              type="date"
              name="reminders.date"
              value={form.reminders.date}
              onChange={handleInputChange}
              className="p-2 border rounded focus:ring focus:ring-blue-200"
            />
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={handleSubmit}
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
          >
            Save
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-md lg:pb-6 lg:pr-6 lg:pl-6 max-h-80 overflow-y-auto sticky-header">
        <table className="min-w-full divide-y divide-gray-200 mt-4">
          <thead className="bg-gray-50 z-10">
            <tr className="bg-gray-50">
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                <input
                  type="text"
                  className="p-2 border rounded focus:ring focus:ring-blue-200 w-full"
                  placeholder="Station"
                  value={searchByStation}
                  onChange={(e) => setSearchByStation(e.target.value)}
                />
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                <input
                  type="text"
                  className="p-2 border rounded focus:ring focus:ring-blue-200 w-full"
                  placeholder="Dealer"
                  value={searchByDealer}
                  onChange={(e) => setSearchByDealer(e.target.value)}
                />
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Response</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Remarks</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 max-h-60 overflow-y-auto">
            {records
              .filter(
                (record) =>
                  record.station.name.toLowerCase().includes(searchByStation.toLowerCase()) &&
                  (record.recipient.name || record.recipient.businessName)
                    .toLowerCase()
                    .includes(searchByDealer.toLowerCase())
              )
              .map((record) => (
                <tr className="hover:bg-gray-50" key={record._id}>
                  <td className="px-4 py-3">{record.station.name}</td>
                  <td className="px-4 py-3">{record.recipient.businessName || record.recipient.name}</td>
                  <td className="px-4 py-3">{record.callResponse}</td>
                  <td className="px-4 py-3">{record.remarks}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default TeleCallingSalesManagement;